import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://argia-admin.com";
const SUFFIX = "ARGIA Admin";

const META = {
  "/": {
    title: "Gestion administrative au Pays Basque — ARGIA Admin",
    description:
      "Gestionnaire administrative indépendante à Guiche (64). Facturation, dossiers, démarches en ligne pour professionnels et particuliers au Pays Basque et Sud Landes. Premier rendez-vous offert.",
  },
  "/services": {
    title: "Services de gestion administrative — Pays Basque & Sud Landes",
    description:
      "Facturation, pré-comptabilité, assistance RH, organisation documentaire, démarches en ligne. Sur site ou à distance, pour les TPE, artisans, indépendants et particuliers.",
  },
  "/tarifs": {
    title: "Tarifs 2026-2027 — forfaits dès 135 € HT/mois, missions à prix fixe",
    description:
      "Forfaits mensuels de 3 à 28 h (135 à 1 050 € HT), abonnements dès 25 €/mois, missions ponctuelles à prix fixe, kits à faire soi-même. Sans engagement, rendez-vous découverte offert.",
  },
  "/a-propos": {
    title: "À propos d'Aurélie Violet — gestionnaire administrative",
    description:
      "Aurélie Violet, gestionnaire administrative indépendante à Guiche (64), membre du réseau national AADPROX, titre RPMS niveau 5, assurance RC Pro.",
  },
  "/rendez-vous": {
    title: "Prendre rendez-vous — premier échange offert",
    description:
      "Réservez votre rendez-vous découverte en ligne en quelques clics : questionnaire rapide et créneaux disponibles en temps réel. Pays Basque, Sud Landes ou à distance.",
  },
  "/faq": {
    title: "Questions fréquentes — gestion administrative",
    description:
      "Missions, zone d'intervention, tarifs, confidentialité, déroulement du premier rendez-vous : toutes les réponses sur l'accompagnement administratif ARGIA Admin.",
  },
  "/mentions-legales": {
    title: "Mentions légales",
    description: "Mentions légales du site ARGIA Admin — Aurélie Violet, Guiche (64).",
  },
  "/politique-confidentialite": {
    title: "Politique de confidentialité",
    description: "Traitement et protection de vos données personnelles (RGPD) sur le site ARGIA Admin.",
  },
};

const LOCAL_BUSINESS = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE}/#business`,
  name: "ARGIA Admin",
  alternateName: "ARGIA Admin — Aurélie Violet",
  description:
    "Gestion administrative indépendante pour professionnels et particuliers : facturation, dossiers, pré-comptabilité, démarches en ligne. Pays Basque et Sud Landes, sur site ou à distance.",
  url: SITE,
  image: `${SITE}/assets/logo-argia.png`,
  logo: `${SITE}/assets/logo-argia.png`,
  telephone: "+33633837456",
  email: "a.violet@aadprox.com",
  priceRange: "€€",
  currenciesAccepted: "EUR",
  founder: { "@type": "Person", name: "Aurélie Violet", jobTitle: "Gestionnaire administrative indépendante" },
  memberOf: { "@type": "Organization", name: "AADPROX" },
  address: {
    "@type": "PostalAddress",
    streetAddress: "95 Chemin des Dames",
    postalCode: "64520",
    addressLocality: "Guiche",
    addressRegion: "Nouvelle-Aquitaine",
    addressCountry: "FR",
  },
  geo: { "@type": "GeoCoordinates", latitude: 43.4783, longitude: -1.2264 },
  areaServed: [
    "Guiche", "Bidache", "Bayonne", "Hasparren", "Saint-Palais", "Peyrehorade",
    "Salies-de-Béarn", "Pays Basque", "Sud Landes", "Béarn",
  ].map((name) => ({ "@type": "City", name })),
  knowsLanguage: "fr",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "12:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "14:00",
      closes: "17:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Prestations de gestion administrative",
    itemListElement: [
      "Gestion administrative courante",
      "Facturation et relances",
      "Pré-comptabilité",
      "Assistance RH",
      "Organisation et dématérialisation documentaire",
      "Démarches administratives en ligne",
    ].map((name) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name } })),
  },
  potentialAction: {
    "@type": "ReserveAction",
    target: `${SITE}/rendez-vous`,
    name: "Prendre rendez-vous",
  },
};

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    const match = selector.match(/\[(name|property|rel)="([^"]+)"\]/);
    if (match) el.setAttribute(match[1], match[2]);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const buildFaqLd = (faqs) =>
  faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }
    : null;

const buildServiceLd = (ville, url) =>
  ville
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: `Gestion administrative à ${ville.name}`,
        serviceType: "Gestion administrative indépendante",
        description: ville.description,
        url,
        provider: { "@id": `${SITE}/#business` },
        areaServed: [ville.name, ...ville.nearby].map((name) => ({ "@type": "City", name })),
      }
    : null;

const buildBreadcrumbLd = (ville, url) =>
  ville
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: `Gestion administrative à ${ville.name}`, item: url },
        ],
      }
    : null;

const applyMeta = (pathname, ville) => {
  const meta = META[pathname] || (ville && { title: ville.title, description: ville.description });
  const url = `${SITE}${pathname === "/" ? "/" : pathname}`;
  const title = meta ? `${meta.title}${meta.title.includes(SUFFIX) ? "" : ` | ${SUFFIX}`}` : `Espace pro | ${SUFFIX}`;
  document.title = title;
  setMeta('meta[name="description"]', "content", meta?.description || "");
  setMeta('link[rel="canonical"]', "href", url);
  setMeta('meta[property="og:title"]', "content", title);
  setMeta('meta[property="og:description"]', "content", meta?.description || "");
  setMeta('meta[property="og:url"]', "content", url);
  setMeta('meta[name="robots"]', "content", meta ? "index, follow" : "noindex, nofollow");
  return url;
};

/** Métadonnées SEO pilotées par la route + données structurées Google. */
export const Seo = ({ faqs, ville }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    const url = applyMeta(pathname, ville);
    setJsonLd("ld-business", LOCAL_BUSINESS);
    setJsonLd("ld-service", buildServiceLd(ville, url));
    setJsonLd("ld-breadcrumb", buildBreadcrumbLd(ville, url));
    setJsonLd("ld-faq", buildFaqLd(faqs));
  }, [pathname, faqs, ville]);

  return null;
};
